import moment from "moment-timezone";
import { SchedulerStoreType } from "../state/scheduler.store";
import { last } from "lodash-es";
import { DateElement } from "../services/api/types/availability.type";
import { Availability } from "../services/api/types/autobookAvailability.type";

export function findNonIntersectingParts(
  start,
  end,
  ranges: Array<{ rangeStart: string; rangeEnd: string }>,
  targetTz
) {
  // Convert input dates to milliseconds for easier comparison
  const startTime = moment(start).valueOf();
  const endTime = moment(end).valueOf();

  // Initialize result array
  const result: { start: string; end: string }[] = [];

  // If no ranges provided, return entire period as invalid
  if (!ranges || ranges.length === 0) {
    result.push({
      start: moment(startTime).tz(targetTz).format(),
      end: moment(endTime).tz(targetTz).format()
    });
    return result;
  }

  // Sort ranges by start time
  const sortedRanges = ranges.sort(
    (a, b) => moment(a.rangeStart).valueOf() - moment(b.rangeStart).valueOf()
  );

  // Make all past times invalid
  const now = moment().valueOf();
  if (startTime < now) {
    result.push({
      start: moment(startTime).tz(targetTz).format(),
      end: moment(Math.min(now, endTime)).tz(targetTz).format()
    });
  }

  // Check gap before first range
  const firstRangeStart = moment(sortedRanges[0].rangeStart).valueOf();
  if (startTime < firstRangeStart) {
    result.push({
      start: moment(startTime).tz(targetTz).format(),
      end: moment(firstRangeStart).tz(targetTz).format()
    });
  }

  // Check gaps between ranges
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    const currentRangeEnd = moment(sortedRanges[i].rangeEnd).valueOf();
    const nextRangeStart = moment(sortedRanges[i + 1].rangeStart).valueOf();

    if (currentRangeEnd < nextRangeStart) {
      result.push({
        start: moment(currentRangeEnd).tz(targetTz).format(),
        end: moment(nextRangeStart).tz(targetTz).format()
      });
    }
  }

  // Check gap after last range
  const lastRangeEnd = moment(
    sortedRanges[sortedRanges.length - 1].rangeEnd
  ).valueOf();
  if (lastRangeEnd < endTime) {
    result.push({
      start: moment(lastRangeEnd).tz(targetTz).format(),
      end: moment(endTime).tz(targetTz).format()
    });
  }

  return result;
}

export function filterPreviouslySelectedSlotFromComputedAvailability(
  sessionData: SchedulerStoreType["sessionData"],
  computedAvailability: DateElement[],
  eventDuration: number
) {
  const prevSlot = last(sessionData?.prevSelectedSlot);

  if (!prevSlot) {
    return computedAvailability // Remove records with no availability
      .filter((date) => date.availability && date.availability.length > 0);
  }

  const basePrevSlotTime = moment
    .utc(prevSlot.day)
    .tz(prevSlot.timeZone, true)
    .add(prevSlot.hour, "hour")
    .add(prevSlot.minute, "minute");

  const prevSlotStartInTimezone = basePrevSlotTime.format();
  const prevSlotEndInTimezone = basePrevSlotTime
    .clone()
    .add(eventDuration, "minute")
    .format();

  const adjComputedAvailability = computedAvailability
    .map((date) => ({
      ...date,
      availability: date.availability
        .map((slot) => {
          const currentSlot = moment(date.date)
            .add(slot.hour, "hour")
            .add(slot.minute, "minute")
            .format();
          if (
            moment(currentSlot).isSame(prevSlotStartInTimezone, "minute") ||
            moment(currentSlot).isBetween(
              prevSlotStartInTimezone,
              prevSlotEndInTimezone,
              "minute"
            )
          ) {
            return { ...slot, isPrevSlot: true };
          }
          return slot;
        })
        .filter((slot) => !slot?.isPrevSlot)
    })) // Remove records with no availability
    .filter((date) => date.availability && date.availability.length > 0);

  return adjComputedAvailability;
}

export const filterAndSortFutureAvailabilities = (
  availabilities: Availability[],
  timezone: string
) => {
  return availabilities
    ?.sort((a, b) => moment(a.start).diff(moment(b.start)))
    ?.filter((availability) =>
      moment(availability.end).tz(timezone).isAfter(moment().tz(timezone))
    );
};
